import { useEffect, useRef, useCallback } from 'react';
import { useAppDispatch } from 'src/hooks';
import {
    decreaseViewPageNumber,
    increaseViewPageNumber,
    clearTargetMessage
} from 'src/redux/chat/slice';
import { ChatSelectors } from 'src/redux/chat/selectors';
import { ROUND_OFF_SCROLL_HEIGHT } from 'src/constants/chat';
import { MessageSchema } from 'src/types/chat';

interface UseChatScrollProps {
    messages: MessageSchema[];
    conversationId: number;
    lastReadMessageId?: number;
    targetMessageId?: number | null;
}

export const useChatScroll = ({
    messages,
    conversationId,
    lastReadMessageId,
    targetMessageId
}: UseChatScrollProps) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const dispatch = useAppDispatch();
    const { hasNextPage, hasPreviousPage } = ChatSelectors(conversationId);

    const loadingRef = useRef(false);
    const hasInitialScrollRef = useRef(false);
    const hasScrolledToLastReadRef = useRef(false);
    const prevMessagesLengthRef = useRef(messages.length);
    const isPrependingRef = useRef(false);
    const isContentShrinkingRef = useRef(false);
    const forceScrollBottomRef = useRef(false);
    const prevScrollHeightRef = useRef(0);
    const prevScrollTopRef = useRef(0);
    const wasAtBottomRef = useRef(true);

    const captureScrollSnapshot = useCallback(() => {
        if (!scrollRef.current) return;
        prevScrollHeightRef.current = scrollRef.current.scrollHeight;
        prevScrollTopRef.current = scrollRef.current.scrollTop;
    }, []);

    const prepareForMessageRemoval = useCallback(() => {
        if (!scrollRef.current) return;
        captureScrollSnapshot();
        isContentShrinkingRef.current = true;
    }, [captureScrollSnapshot]);

    const forceScrollToBottom = useCallback(() => {
        forceScrollBottomRef.current = true;
        wasAtBottomRef.current = true;
        requestAnimationFrame(() => {
            if (scrollRef.current) {
                scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
                captureScrollSnapshot();
            }
            forceScrollBottomRef.current = false;
        });
    }, [captureScrollSnapshot]);

    useEffect(() => {
        hasInitialScrollRef.current = false;
        hasScrolledToLastReadRef.current = false;
        prevMessagesLengthRef.current = 0;
        isPrependingRef.current = false;
        isContentShrinkingRef.current = false;
        forceScrollBottomRef.current = true;
        prevScrollHeightRef.current = 0;
        prevScrollTopRef.current = 0;
        loadingRef.current = false;
    }, [conversationId]);

    useEffect(() => {
        const container = scrollRef.current;
        if (!container) {
            return;
        }

        const prevMessagesCount = prevMessagesLengthRef.current;
        const currentMessagesCount = messages.length;
        const previousHeight = prevScrollHeightRef.current;
        const previousTop = prevScrollTopRef.current;

        const resetLoadingState = () => {
            loadingRef.current = false;
        };

        if (!hasInitialScrollRef.current) {
            if (currentMessagesCount === 0) {
                return;
            }
            requestAnimationFrame(() => {
                if (!scrollRef.current) return;

                const scrollTarget = targetMessageId ?? lastReadMessageId;

                if (scrollTarget && !hasScrolledToLastReadRef.current) {
                    const messageElement = scrollRef.current.querySelector(
                        `[data-message-id="${scrollTarget}"]`
                    );

                    if (messageElement) {
                        messageElement.scrollIntoView({
                            behavior: 'smooth',
                            block: 'center'
                        });
                        hasScrolledToLastReadRef.current = true;
                        forceScrollBottomRef.current = false;
                        wasAtBottomRef.current = false;
                        if (targetMessageId) {
                            dispatch(clearTargetMessage());
                        }
                    } else {
                        scrollRef.current.scrollTop =
                            scrollRef.current.scrollHeight;
                    }
                } else {
                    scrollRef.current.scrollTop =
                        scrollRef.current.scrollHeight;
                }

                hasInitialScrollRef.current = true;
                captureScrollSnapshot();
            });
            prevMessagesLengthRef.current = currentMessagesCount;
            return;
        }

        if (
            currentMessagesCount > prevMessagesCount &&
            isPrependingRef.current
        ) {
            requestAnimationFrame(() => {
                if (!scrollRef.current) return;
                const addedHeight =
                    scrollRef.current.scrollHeight - previousHeight;
                if (addedHeight > 0) {
                    scrollRef.current.scrollTop = previousTop + addedHeight;
                }
                isPrependingRef.current = false;
                resetLoadingState();
            });
        } else if (isContentShrinkingRef.current) {
            requestAnimationFrame(() => {
                if (!scrollRef.current) return;
                const heightDelta =
                    scrollRef.current.scrollHeight - previousHeight;
                if (heightDelta < 0) {
                    const nextTop = previousTop + heightDelta;
                    scrollRef.current.scrollTop = Math.max(0, nextTop);
                }
                isContentShrinkingRef.current = false;
                resetLoadingState();
            });
        } else if (currentMessagesCount > prevMessagesCount) {
            requestAnimationFrame(() => {
                if (!scrollRef.current) return;

                if (forceScrollBottomRef.current || wasAtBottomRef.current) {
                    scrollRef.current.scrollTop =
                        scrollRef.current.scrollHeight;
                }

                resetLoadingState();
            });
        } else {
            if (forceScrollBottomRef.current) {
                requestAnimationFrame(() => {
                    if (scrollRef.current) {
                        scrollRef.current.scrollTop =
                            scrollRef.current.scrollHeight;
                    }
                });
            }
            resetLoadingState();
        }

        prevMessagesLengthRef.current = currentMessagesCount;
        captureScrollSnapshot();
    }, [
        dispatch,
        messages,
        captureScrollSnapshot,
        lastReadMessageId,
        targetMessageId
    ]);

    useEffect(() => {
        if (!scrollRef.current) return;
        captureScrollSnapshot();
    }, [messages.length, captureScrollSnapshot]);

    const handleScroll = useCallback(() => {
        if (!scrollRef.current || loadingRef.current) return;

        const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
        const scrollBottom = scrollHeight - clientHeight;

        const atTop = scrollTop <= 10;
        const atBottom = scrollTop >= scrollBottom - ROUND_OFF_SCROLL_HEIGHT;
        wasAtBottomRef.current = atBottom;

        if (atTop && hasPreviousPage) {
            loadingRef.current = true;
            isPrependingRef.current = true;
            captureScrollSnapshot();
            dispatch(
                decreaseViewPageNumber({ conversationId: conversationId })
            );
        }

        if (atBottom && hasNextPage) {
            loadingRef.current = true;
            isPrependingRef.current = false;
            dispatch(
                increaseViewPageNumber({ conversationId: conversationId })
            );
        }
    }, [
        hasPreviousPage,
        hasNextPage,
        dispatch,
        captureScrollSnapshot,
        conversationId
    ]);

    useEffect(() => {
        const element = scrollRef.current;
        if (!element) return;

        element.addEventListener('scroll', handleScroll);
        return () => {
            element.removeEventListener('scroll', handleScroll);
        };
    }, [handleScroll]);

    return {
        scrollRef,
        prepareForMessageRemoval,
        forceScrollToBottom
    };
};
